import { ref } from 'vue';

import { createChatRoom, fetchSelfServiceContractPricingV2 } from '@/api/apiService';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { ChatQuery, ChatResult } from '@/types/components';
import { IResponseContractCustomerPricing } from '@/types/response';
import { CHAT_PRICE_KEY_MAP } from '@/utils/constants/chat';

export const useCreateChat = () => {
  const userStore = useUserStore();
  const subscriptionStore = useSubscriptionStore();

  const { fetchSubscriptionInfo } = useSubscriptionStore();

  const unitPriceList = ref<IResponseContractCustomerPricing[]>([]);

  const getChatFormData = (form): FormData => {
    const { id = '', callType = '', title = '', options, maxParticipants = '' } = form.value;

    const formData = new FormData();
    formData.append(
      'body',
      JSON.stringify({
        plan: `${id}${maxParticipants}`,
        callType,
        title,
        description: '',
        keywords: [],
        options,
        removeEntranceLogoImage: false,
        removeCallLogoImage: false,
      })
    );

    return formData;
  };

  const fetchChatUnitPrice = async () => {
    try {
      const { data } = await fetchSelfServiceContractPricingV2();
      unitPriceList.value = data;
    } catch (error) {
      unitPriceList.value = [];
      console.error(error);
    }
  };

  const hasEnoughCredits = async (planId: string): Promise<boolean> => {
    try {
      await fetchSubscriptionInfo();

      if (unitPriceList.value.length === 0) {
        await fetchChatUnitPrice();
      }

      const { postpaidUnitPrice } = unitPriceList.value.find(({ planId: id }) => id === planId) || {
        postpaidUnitPrice: 0,
      };

      return (
        !!subscriptionStore.currentCredits &&
        !!postpaidUnitPrice &&
        Number(subscriptionStore.currentCredits) >= Number(postpaidUnitPrice)
      );
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const createChat = async (form: FormData, sourceLanguage): Promise<ChatResult> => {
    const { data, success, errorCode } = await createChatRoom(form);

    if (!success) {
      if (errorCode === 1021) {
        return { success: false, error: 'alert_duplicate_name_exists' };
      }

      return { success: false, error: 'UNKNOWN_ERROR' };
    }

    const { code, planId } = data[0];

    if (!(await hasEnoughCredits(planId))) {
      return { success: false, error: 'error_no_credit_modal_title', errorType: 'credit' };
    }

    const query = await getChatQuery(code, planId, sourceLanguage);

    return { success: true, query };
  };

  const enterChat = async (type, sourceLanguage, targetLanguage): Promise<ChatResult> => {
    const planId = `${CHAT_PRICE_KEY_MAP[type]}1`;

    if (!(await hasEnoughCredits(planId))) {
      return { success: false, error: 'error_no_credit_modal_title', errorType: 'credit' };
    }

    const query = await getChatQuery(userStore.userId, planId, sourceLanguage, targetLanguage);

    return { success: true, query };
  };

  const getChatQuery = (code: string, planId: string, sourceLanguage?: string, targetLanguage?: string): ChatQuery => {
    return {
      code,
      planId,
      ...(sourceLanguage && { sourceLanguage }),
      ...(targetLanguage && { targetLanguage }),
    };
  };

  return {
    getChatFormData,
    unitPriceList,
    enterChat,
    createChat,
    hasEnoughCredits,
    fetchChatUnitPrice,
    getChatQuery,
  };
};
